import classNames from './UserInfo.module.scss';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import cls from 'classnames';
import { useAppSelector } from '@/store';
import { authSelector, interfaceLanguageSelector } from '@/store/reducers/auth';

import Badge from './Badge/Badge';
import Detail from './Detail/Detail';
import DetailSeperator from '@/components/DetailSeperator/DetailSeperator';
import UserAvatar from '@/components/UserAvatar/UserAvatar';

import blueStar from '@/assets/svg/star.svg';
import coin from '@/assets/svg/coin.svg';
import UseCurrentRoute from '@/hooks/UseCurrentRoute';
import { ROUTE_NAMES } from '@/router';
import UseScreenSize from '@/hooks/UseScreenSize';
import common from '@/utils/common';

interface UserInfoProps {
  isTeacher?: boolean;
  isMobile?: boolean;
  fullname: string;
  firstName?: string;
  lastName?: string;
  profileImage?: string | null;
  backgroundColor?: string | null;
  notificationsAmount?: number;
  level?: number | string;
  grade?: string;
  coins?: number;
  badges?: number;
  classesAmount?: number;
  studentsAmount?: number;
  schoolName?: string;
  showBadges?: boolean;
  showDetails?: boolean;
}

const UserInfo = ({
  isTeacher = false,
  isMobile = false,
  profileImage,
  backgroundColor,
  fullname,
  firstName,
  lastName,
  level,
  grade,
  coins,
  classesAmount,
  studentsAmount,
  schoolName,
  showBadges = true,
  showDetails = true,
}: UserInfoProps) => {
  const { t } = useTranslation();
  const currentRoute = UseCurrentRoute();
  const interfaceLanguage = useAppSelector(interfaceLanguageSelector);
  const { user } = useAppSelector(authSelector);
  const isGoogle = common.isGoogle(user);

  const totalStars = user?.progress?.achievedStars;
  const { isDesktop } = UseScreenSize();
  const isMobileAndRouteIsProfile = !isDesktop && currentRoute === ROUTE_NAMES.PROFILE;

  const isProfilePage = currentRoute === ROUTE_NAMES.PROFILE;

  let gradeText = '';

  const gradeNum = grade ? parseInt(grade, 10) : null;
  const isNoneGrade = gradeNum === 14;

  if (isTeacher) {
    gradeText = studentsAmount?.toString() ?? '';
  } else if (!isNoneGrade && !isGoogle) {
    if (gradeNum && gradeNum >= 1 && gradeNum <= 14) {
      gradeText = t(`grade_${gradeNum}`);
    } else if (grade) {
      // Fallback for legacy string values
      gradeText = common.getGradeText(grade, t);
    }
  }
  return (
    <div
      className={classes(classNames.header, {
        [classNames.mobile]: isMobile,
        [classNames.isTeacherHeader]: isTeacher,
      })}
    >
      {!isTeacher && (
        <UserAvatar
          className={classNames.profileImage}
          img={profileImage}
          backgroundColor={backgroundColor}
          initials={common.getInitials(firstName, lastName)}
          allowEdit={isTeacher || isProfilePage}
        />
      )}
      <div
        className={classes(classNames.info, [classNames[`info-${interfaceLanguage?.direction}`]])}
      >
        {isTeacher && <h2 className={classNames.fullname}>{t('helloTeacher')}</h2>}
        <h2 className={classNames.fullname}>{fullname}</h2>

        {showDetails && !isMobileAndRouteIsProfile && (
          <>
            {isTeacher && schoolName && <h1>{schoolName}</h1>}
            <div className={classNames.details}>
              <Detail
                topic={isTeacher ? t('classes') : t('level')}
                value={isTeacher ? classesAmount ?? 0 : level}
              />
              {gradeText && (
                <>
                  <DetailSeperator />
                  <Detail topic={isTeacher ? t('students') : t('grade')} value={gradeText} />
                </>
              )}
            </div>
          </>
        )}
        {showBadges && !isTeacher && !isMobileAndRouteIsProfile && (
          <div className={classNames.badges}>
            <Badge type='stars' image={blueStar} amount={totalStars} />
            <Badge type='coins' image={coin} amount={coins} />
          </div>
        )}
      </div>
    </div>
  );
};

export default UserInfo;
